<template>
  <el-container class="main-layout">
    <!-- Mobile Overlay -->
    <div 
      class="sidebar-overlay" 
      :class="{ 'is-visible': sidebarOpened && !isDesktop }"
      @click="closeSidebarMobile"
    ></div>
    
    <!-- Modern Sidebar with gradient -->
    <el-aside 
      :width="isDesktop ? (sidebarCollapsed ? '64px' : '240px') : '280px'" 
      class="sidebar"
      :class="{ 
        'is-collapsed': sidebarCollapsed && isDesktop,
        'is-opened': sidebarOpened && !isDesktop,
        'is-mobile': !isDesktop
      }"
    >
      <div class="logo-section">
        <div class="logo-wrapper">
          <img :src="clubLogoUrl || defaultLogo" alt="Yolal Equestrian" class="logo-img" v-if="!sidebarCollapsed">
          <img :src="clubLogoUrl || defaultLogo" alt="YE" class="logo-short-img" v-else>
          <div class="logo-text" v-if="!sidebarCollapsed">
            <h1 class="app-title">{{ clubName || 'Yolal' }}</h1>
            <p class="app-subtitle">{{ $t('dashboard.appSubtitle') }}</p>
          </div>
        </div>
      </div>
      
      <el-menu
        :default-active="activeMenu"
        :collapse="sidebarCollapsed && isDesktop"
        :collapse-transition="false"
        router
        class="sidebar-menu"
        background-color="#2d3748"
        text-color="#cbd5e0"
        active-text-color="#ffffff"
        @select="handleMenuSelect"
      >
        <el-menu-item index="/dashboard" class="menu-item">
          <el-icon class="menu-icon"><HomeFilled /></el-icon>
          <span class="menu-title">{{ $t('dashboard.menu.overview') }}</span>
        </el-menu-item>
        
        <el-menu-item index="/booking" class="menu-item">
          <el-icon class="menu-icon"><Notebook /></el-icon>
          <span class="menu-title">{{ $t('dashboard.menu.bookings') }}</span>
        </el-menu-item>
        
        <el-sub-menu index="vip-management" class="menu-item">
          <template #title>
            <i class="iconfont icon-vip-management menu-icon"></i>
            <span class="menu-title">{{ $t('dashboard.menu.vip') }}</span>
          </template>
          <el-menu-item index="/vip">
            <span>{{ $t('vip.packages') }}</span>
          </el-menu-item>
          <el-menu-item index="/vip/package-types">
            <span>{{ $t('vip.packageTypes') }}</span>
          </el-menu-item>
          <el-menu-item index="/vip/statistics">
            <span>{{ $t('vip.statisticsTitle') }}</span>
          </el-menu-item>
          <el-menu-item index="/vip/admin-purchase">
            <span>{{ $t('vip.adminPurchase') }}</span>
          </el-menu-item>
        </el-sub-menu>
        
        <el-menu-item index="/schedule/weekly" class="menu-item">
          <el-icon class="menu-icon"><Calendar /></el-icon>
          <span class="menu-title">{{ $t('dashboard.menu.weeklycalendar') }}</span>
        </el-menu-item>
        
        <el-menu-item index="/schedule/unavailable" class="menu-item">
          <el-icon class="menu-icon"><AlarmClock /></el-icon>
          <span class="menu-title">{{ $t('dashboard.menu.setunavailable') }}</span>
        </el-menu-item>
        
        <el-menu-item index="/customer" class="menu-item">
          <i class="iconfont icon-helmet menu-icon"></i>
          <span class="menu-title">{{ $t('dashboard.menu.customers') }}</span>
        </el-menu-item>
        
        <el-menu-item index="/teacher" class="menu-item">
          <i class="iconfont icon-teacher menu-icon"></i>
          <span class="menu-title">{{ $t('dashboard.menu.teachers') }}</span>
        </el-menu-item>
        
        <el-menu-item index="/vip/audit-logs" class="menu-item">
          <el-icon class="menu-icon"><Document /></el-icon>
          <span class="menu-title">{{ $t('vip.auditLogs') }}</span>
        </el-menu-item>
        
        <el-menu-item index="/horse" class="menu-item">
          <i class="iconfont icon-horse menu-icon"></i>
          <span class="menu-title">{{ $t('dashboard.menu.horses') }}</span>
        </el-menu-item>
        
        <el-menu-item index="/posters" class="menu-item">
          <el-icon class="menu-icon"><Picture /></el-icon>
          <span class="menu-title">{{ $t('dashboard.menu.posters') }}</span>
        </el-menu-item>
        
        <el-menu-item index="/settings" class="menu-item">
          <el-icon class="menu-icon"><Tools /></el-icon>
          <span class="menu-title">{{ $t('dashboard.menu.settings') }}</span>
        </el-menu-item>
      </el-menu>
      
      <div class="sidebar-footer">
        <div class="version-info" v-if="!sidebarCollapsed && isDesktop" @click="showAboutDialog = true">
          <span class="version-label">Version</span>
          <span class="version-number">v{{ appVersion }}</span>
        </div>
      </div>
    </el-aside>
    
    <!-- Main Content Area -->
    <el-container class="content-wrapper" :class="{ 'is-mobile-open': sidebarOpened && !isDesktop }">
      <!-- Modern Header -->
      <el-header class="header">
        <div class="header-left">
          <el-button class="collapse-btn" @click="toggleSidebar" circle>
            <el-icon><Fold v-if="sidebarCollapsed" /><Expand v-else /></el-icon>
          </el-button>
          <breadcrumb class="hide-mobile" />
        </div>
        <div class="header-right">
          <!-- Theme Switcher - El Switch Component -->
          <el-switch
            v-model="isDarkMode"
            @change="toggleTheme"
            class="theme-toggle-switch"
          >
            <template #active-action>
              <Moon style="width: 17px; height: 17px;" />
            </template>
            <template #inactive-action>
              <Sunny style="width: 17px; height: 17px;" />
            </template>
          </el-switch>
          <div class="user-actions">
            <el-dropdown @command="handleCommand" trigger="click">
              <div class="user-profile">
                <div class="user-details hide-mobile">
                  <span class="user-name">{{ userStore.username }}</span>
                </div>
                <el-icon class="dropdown-icon"><ArrowDown /></el-icon>
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="settings">
                    <el-icon><Setting /></el-icon>
                    {{ $t('common.settings') }}
                  </el-dropdown-item>
                  <el-dropdown-item divided command="logout">
                    <el-icon><SwitchButton /></el-icon>
                    {{ $t('common.logout') }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>
      </el-header>
      
      <!-- Main Content -->
      <el-main class="main-content">
        <router-view v-slot="{ Component }">
          <component :is="Component" />
        </router-view>
      </el-main>
      
      <!-- Global Customer Service FAB -->
      <CustomerServiceFAB />
    </el-container>
  </el-container>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import Breadcrumb from '@/components/Breadcrumb.vue'
import CustomerServiceFAB from '@/components/CustomerServiceFAB.vue'
import { UserFilled, Tools, SwitchButton, Sunny, Moon, Setting, Notebook } from '@element-plus/icons-vue'
import { getClubInfo } from '@/api/club'
import packageJson from '../../package.json'

const appVersion = packageJson.version

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const { t, tm } = useI18n()
const userStore = useUserStore()

const currentTheme = ref(appStore.theme || 'light')
const isDarkMode = computed({
  get: () => currentTheme.value === 'dark',
  set: (value) => {
    currentTheme.value = value ? 'dark' : 'light'
  }
})
const clubName = ref('')
const clubLogoUrl = ref('')
const defaultLogo = new URL('@/assets/logo.png', import.meta.url).href

const toggleTheme = (value) => {
  const newTheme = value ? 'dark' : 'light'
  currentTheme.value = newTheme
  appStore.setTheme(newTheme)
}

const windowWidth = ref(window.innerWidth)
const isDesktop = computed(() => windowWidth.value >= 992)

const handleResize = () => {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

watch(isDesktop, (newValue) => {
  if (newValue) {
    appStore.openSidebarMobile()
  }
})

const sidebarCollapsed = computed(() => appStore.sidebarCollapsed)
const sidebarOpened = computed(() => appStore.sidebarOpened)
const activeMenu = computed(() => route.path)
const showAboutDialog = ref(false)

const toggleSidebar = () => {
  if (!isDesktop.value) {
    appStore.toggleSidebarMobile()
  } else {
    appStore.toggleSidebar()
  }
}

const closeSidebarMobile = () => {
  if (!isDesktop.value) {
    appStore.closeSidebarMobile()
  }
}

const handleMenuSelect = () => {
  if (!isDesktop.value) {
    appStore.closeSidebarMobile()
  }
}

const handleCommand = (command) => {
  switch (command) {
    case 'settings':
      router.push('/settings')
      break
    case 'logout':
      handleLogout()
      break
  }
}

const handleLogout = async () => {
  try {
    await ElMessageBox.confirm(
      t('common.logoutConfirm'),
      t('common.logout'),
      {
        confirmButtonText: t('common.logout'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      }
    )
    userStore.clearSession()
    router.push('/login')
    ElMessage.success(t('common.logoutSuccess'))
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Logout error:', error)
    }
  }
}

// Fetch club info on mount
const fetchClubInfo = async () => {
  try {
    const res = await getClubInfo()
    if (res.success && res.data) {
      // Handle both array and single object response
      const clubData = Array.isArray(res.data) ? res.data[0] : res.data
      if (clubData) {
        if (clubData.name) {
          clubName.value = clubData.name
        }
        if (clubData.logoImageUrl) {
          clubLogoUrl.value = clubData.logoImageUrl
        }
        // Store full club info in app store for operationMode access
        appStore.setClubInfo(clubData)
      }
    }
  } catch (error) {
    console.error('Failed to fetch club info:', error)
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  fetchClubInfo()
})
</script>

<style lang="scss" scoped>
// Ensure circular buttons maintain their shape on all screen sizes
:deep(.el-button.is-circle) {
  border-radius: 50% !important;
  aspect-ratio: 1 / 1;
  
  @media (max-width: 576px) {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    min-height: 40px !important;
  }
}

.main-layout {
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height for mobile */
  height: -webkit-fill-available;
  display: flex;
  position: relative;
  overflow: hidden;
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  
  &.is-visible {
    opacity: 1;
    visibility: visible;
  }
  
  @media (min-width: 992px) {
    display: none;
  }
}

.sidebar {
  background: linear-gradient(180deg, #2d3748 0%, #1a202c 100%);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: relative;
  z-index: 1000;
  
  @media (max-width: 991px) {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    transform: translateX(-100%);
    
    &.is-opened {
      transform: translateX(0);
    }
  }
  
  // Dark mode adjustments
  html.dark & {
    background: linear-gradient(180deg, #1a202c 0%, #2d3748 100%);
  }
  
  .logo-section {
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
    
    @media (max-width: 991px) {
      height: 64px;
    }
    
    .logo-wrapper {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 0 20px;
      justify-content: flex-start;
      
      @media (max-width: 991px) {
        padding: 0 16px;
        gap: 10px;
      }
      
      .logo-img {
        height: 45px;
        width: 45px;
        border-radius: 8px;
        object-fit: cover;
        box-shadow: 0 2px 8px rgba(66, 153, 225, 0.3);
      }
      
      .logo-short-img {
        height: 36px;
        width: 36px;
        border-radius: 8px;
        object-fit: cover;
        box-shadow: 0 2px 8px rgba(66, 153, 225, 0.3);
      }
      
      .logo-text {
        flex: 1;
        min-width: 0;
        
        .app-title {
          font-size: var(--font-size-base);
          font-weight: 600;
          color: #fff;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          
          @media (max-width: 991px) {
            font-size: var(--font-size-sm);
          }
        }
        
        .app-subtitle {
          font-size: var(--font-size-xs);
          color: #a0aec0;
          margin: 2px 0 0 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          
          @media (max-width: 991px) {
            font-size: var(--font-size-xs);
          }
        }
      }
    }
  }
  
  .sidebar-menu {
    flex: 1;
    border-right: none;
    overflow-y: auto;
    overflow-x: hidden;
    flex-shrink: 0;
    
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
    }
    
    .menu-item {
      margin: 0;
      border-radius: 0;
      transition: all 0.3s;
      border-left: 4px solid transparent;
            
      &:hover {
        background-color: #4a5568 !important;
      }
            
      &.is-active {
        background-color: #4a5568 !important;
        border-left-color: #4299e1;
      }
            
      .menu-icon {
        font-size: var(--font-size-lg);
        margin-right: 12px;
        color: inherit;
      }
            
      .menu-title {
        font-size: var(--font-size-base);
        font-weight: 500;
      }
    }
  }
  
  .sidebar-footer {
    padding: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    
    .version-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .version-label {
        font-size: var(--font-size-xs);
        color: #666;
      }
      
      .version-number {
        font-size: var(--font-size-xs);
        color: #409EFF;
        font-weight: 500;
      }
    }
  }
}

.content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  transition: margin-left 0.3s ease;
  
  @media (max-width: 991px) {
    margin-left: 0 !important;
    
    &.is-mobile-open {
      margin-left: 0;
    }
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--bg-color-white, #fff);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 0 24px;
  height: 70px;
  flex-shrink: 0;
  transition: background-color 0.3s ease;
  
  @media (max-width: 576px) {
    padding: 0 12px;
    height: 60px;
  }
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
    flex: 1;
    
    .collapse-btn {
      font-size: var(--font-size-lg);
      cursor: pointer;
      transition: all 0.3s;
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      
      @media (max-width: 576px) {
        width: 40px !important;
        height: 40px !important;
        min-width: 40px !important;
        min-height: 40px !important;
      }
      
      &:hover {
        transform: scale(1.1);
      }
    }
  }
  
  .header-right {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-shrink: 0;
    
    @media (max-width: 576px) {
      gap: 12px;
    }
    
    .theme-toggle-switch {
      margin-right: 8px;
      
      @media (max-width: 576px) {
        margin-right: 4px;
      }
      
      :deep(.el-switch__core) {
        width: 60px;
        height: 28px;
        border-radius: 19px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        border: 1px solid #dcdfe6;
        background-color: #dcdfe6;
        
        html.dark & {
          border-color: #4c4c4c;
          background-color: #4c4c4c;
        }
      }
      
      :deep(.el-switch__action) {
        width: 24px;
        height: 24px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        background-color: #ffffff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
        display: flex;
        align-items: center;
        justify-content: center;
        
        html.dark & {
          background-color: #ffffff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
          margin-left: -10px;
        }
      }
      
      // On state - dark mode active
      :deep(.is-checked) {
        .el-switch__core {
          background-color: #409EFF;
          border-color: #409EFF;
          
          html.dark & {
            background-color: #E6A23C;
            border-color: #E6A23C;
          }
        }
      }
    }
    
    .user-actions {
      .user-profile {
        display: flex;
        align-items: center;
        gap: 12px;
        cursor: pointer;
        padding: 8px 12px;
        border-radius: 8px;
        transition: all 0.3s;
        white-space: nowrap;
        
        &:hover {
          background-color: var(--bg-color-light, #f5f7fa);
        }
        
        .user-avatar {
          box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
          flex-shrink: 0;
          width: 36px !important;
          height: 36px !important;
          min-width: 36px !important;
          min-height: 36px !important;
          border-radius: 50% !important;
          border: 2px solid #ffffff;
          cursor: pointer;
          transition: all 0.3s ease;
          
          &:hover {
            transform: scale(1.1);
            box-shadow: 0 4px 12px rgba(64, 158, 255, 0.5);
          }
          
          @media (max-width: 576px) {
            width: 36px !important;
            height: 36px !important;
            min-width: 36px !important;
            min-height: 36px !important;
          }
        }
        
        .user-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
          overflow: hidden;
          line-height: 1.4;
          
          .user-name {
            font-size: var(--font-size-sm);
            font-weight: 600;
            color: #333;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            line-height: 1.4;
          }
        }
        
        .dropdown-icon {
          font-size: var(--font-size-sm);
          color: #909399;
          transition: transform 0.3s;
          flex-shrink: 0;
        }
      }
    }
  }
}

.main-content {
  flex: 1;
  background: var(--bg-color-page, #f0f2f5);
  padding: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  transition: background-color 0.3s ease;
  -webkit-overflow-scrolling: touch;
  /* Add bottom padding on mobile to prevent content from being hidden behind home indicator */
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
  
  @media (max-width: 768px) {
    padding: 16px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
  }
  
  @media (max-width: 576px) {
    padding: 12px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
}

.fade-transform-enter-active,
.fade-transform-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-transform-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-transform-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

@media (max-width: 991px) {
  .content-wrapper {
    margin-left: 0 !important;
  }
}

@media (max-width: 576px) {
  .sidebar .logo-section .logo-wrapper {
    padding: 0 12px;
    
    .logo-img {
      height: 36px !important;
      width: 36px !important;
    }
    
    .logo-short-img {
      height: 32px !important;
      width: 32px !important;
    }
    
    .logo-text {
      .app-title {
        font-size: 14px !important;
      }
      
      .app-subtitle {
        font-size: 10px !important;
      }
    }
  }
  
  .sidebar-menu {
    .menu-item {
      .menu-icon {
        font-size: 20px !important;
        margin-right: 16px !important;
      }
      
      .menu-title {
        font-size: 16px !important;
      }
    }
  }
}
</style>
